<script>
import { slide } from 'svelte/transition';
import { onMount, afterUpdate } from 'svelte';
import ThreadItem from './ThreadItem.svelte';

// Props
export let items = [];     // Array of items to display in the thread
export let loading = false; // Whether to show loading state
export let parentAlignment = 24; // Position of vertical line relative to left edge of parent term, in pixels
export let itemDistanceFromLine = 16; // Distance from vertical line to child items

// SVG dimensions and positioning
let containerElement;
let svgHeight = 0;
let connectorPositions = [];
let verticalLineEndY = 0;

// Calculate SVG dimensions and connector positions
function calculateSVGLayout() {
    if (!containerElement) return;
    
    const threadContent = containerElement.querySelector('.thread-content');
    if (!threadContent) return;
    
    // Calculate total height needed for SVG
    const contentHeight = threadContent.offsetHeight;
    const parentSpacing = 16; // --thread-parent-spacing
    const extensionAboveParent = 8; // Additional extension to touch parent pill
    
    // Ensure minimum height for SVG
    svgHeight = Math.max(contentHeight + parentSpacing + extensionAboveParent, 24);
    
    // Calculate connector positions for each item
    connectorPositions = [];
    const itemElements = threadContent.querySelectorAll('.thread-item-wrapper');
    
    // Only calculate positions if we have items to avoid empty arrays
    if (itemElements.length > 0) {
        const containerRect = containerElement.getBoundingClientRect();
        let lastConnectorY = 0;
        
        itemElements.forEach((itemElement, index) => {
            // Safety check for element existence
            if (!itemElement) return;
            
            const itemRect = itemElement.getBoundingClientRect();
            
            // Calculate Y position relative to SVG coordinate system
            const relativeY = itemRect.top - containerRect.top + parentSpacing + extensionAboveParent;
            const itemCenterY = relativeY + (itemElement.offsetHeight / 2);
            
            // Ensure Y position is within reasonable bounds
            if (itemCenterY >= 0 && itemCenterY <= svgHeight) {
                connectorPositions.push({
                    y: itemCenterY,
                    index: index
                });
                
                // Track the bottommost connector position
                lastConnectorY = Math.max(lastConnectorY, itemCenterY);
            }
        });
        
        // Set vertical line to end at the last connector
        verticalLineEndY = lastConnectorY;
    } else {
        // If no items, don't show vertical line
        verticalLineEndY = 0;
    }
}

// Reactive updates
$: if (items.length > 0 || loading) {
    // Recalculate when items change
    setTimeout(calculateSVGLayout, 0);
}

onMount(() => {
    calculateSVGLayout();
});

afterUpdate(() => {
    calculateSVGLayout();
});
</script>

<div class="thread-container" data-testid="thread-container" bind:this={containerElement} transition:slide={{ duration: 200 }}
     style="--parent-alignment: {parentAlignment}px; 
            --item-distance-from-line: {itemDistanceFromLine}px;">
    
    <!-- SVG for thread lines -->
    {#if svgHeight > 0}
        <svg class="thread-svg" width="100%" height="{svgHeight}px">
            <!-- Main vertical line (only if there are items) -->
            {#if verticalLineEndY > 0}
                <line 
                    x1="{parentAlignment}" 
                    y1="0" 
                    x2="{parentAlignment}" 
                    y2="{verticalLineEndY}"
                    stroke="var(--color-terms-thread-line)" 
                    stroke-width="2"
                />
            {/if}
            
            <!-- Horizontal connectors for each item -->
            {#each connectorPositions as connector}
                <line 
                    x1="{parentAlignment}" 
                    y1="{connector.y}" 
                    x2="{parentAlignment + itemDistanceFromLine}" 
                    y2="{connector.y}"
                    stroke="var(--color-terms-thread-line)" 
                    stroke-width="2"
                />
            {/each}
        </svg>
    {/if}
    
    <div class="thread-content" data-testid="thread-content">
        {#if loading}
            <ThreadItem loading={true} index={0} />
        {:else if items.length > 0}
            <div class="thread-items-list">
                {#each items.sort((a, b) => b.length - a.length) as item, index}
                    <ThreadItem item={item} index={index} />
                {/each}
            </div>
        {/if}
    </div>
</div>

<style>
.thread-container {
    position: relative;
    display: flex;
    width: 100%; /* Take full width of parent */
    margin-top: var(--thread-parent-spacing, var(--spacing-4));
    margin-left: 0;
    overflow: visible;
}

.thread-svg {
    position: absolute;
    top: calc(-1 * var(--thread-parent-spacing, var(--spacing-4)) - var(--spacing-2));
    left: 0;
    z-index: var(--z-index-0);
    pointer-events: none;
}

.thread-content {
    position: relative;
    z-index: var(--z-index-10);
    flex: 1; /* Take remaining width after margins */
    margin-left: calc(var(--parent-alignment, var(--spacing-4)) + var(--item-distance-from-line, var(--spacing-4)));
}

.thread-items-list {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
}
</style>